import * as fs from "node:fs/promises";
import * as path from "node:path";
import { parse as parseYaml, stringify as stringifyYaml } from "yaml";
import { LocalCliError } from "./errors.js";

export const FOUNDRY_CONFIG_KEYS = ["tenantId", "clientId", "projectEndpoint"] as const;
export type FoundryConfigKey = (typeof FOUNDRY_CONFIG_KEYS)[number];

export interface FoundryConfig {
  tenantId: string;
  clientId: string;
  projectEndpoint: string;
  projectName: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function configDir(): string {
  const home = process.env.HOME ?? process.env.USERPROFILE ?? "";
  return path.join(home, ".m8t-stack");
}

export function getFoundryConfigPath(): string {
  return path.join(configDir(), "config.yaml");
}

export function getSeedPath(): string {
  return path.join(configDir(), "foundry", "seed.yaml");
}

/** Parse the trailing `/api/projects/<name>` segment. Throws on a bad URL. */
export function parseProjectName(endpoint: string): string {
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    throw new LocalCliError({
      code: "CONFIG_ENDPOINT_INVALID",
      message: `projectEndpoint is not a valid URL: ${endpoint}`,
      hint: "Expected https://<account>.services.ai.azure.com/api/projects/<project>",
    });
  }
  const segs = url.pathname.split("/").filter(Boolean);
  const name = segs[segs.length - 1];
  if (!name) {
    throw new LocalCliError({
      code: "CONFIG_ENDPOINT_NO_PROJECT",
      message: `projectEndpoint has no project segment: ${endpoint}`,
      hint: "It should end with /api/projects/<project-name>.",
    });
  }
  return name;
}

export function validateConfigValue(key: FoundryConfigKey, value: string): void {
  if (key === "projectEndpoint") {
    parseProjectName(value); // throws if invalid
    return;
  }
  if (!UUID_RE.test(value)) {
    throw new LocalCliError({
      code: "CONFIG_VALUE_INVALID",
      message: `${key} must be a UUID, got: ${value}`,
    });
  }
}

export async function readFoundryConfig(): Promise<FoundryConfig | null> {
  let raw: string;
  try {
    raw = await fs.readFile(getFoundryConfigPath(), "utf8");
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return null;
    throw e;
  }
  let parsed: unknown;
  try {
    parsed = parseYaml(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const o = parsed as Record<string, unknown>;
  if (
    typeof o.tenantId !== "string" ||
    typeof o.clientId !== "string" ||
    typeof o.projectEndpoint !== "string" ||
    o.tenantId.length === 0 ||
    o.clientId.length === 0 ||
    o.projectEndpoint.length === 0
  ) {
    return null;
  }
  let projectName: string;
  try {
    projectName = parseProjectName(o.projectEndpoint);
  } catch {
    return null;
  }
  return {
    tenantId: o.tenantId,
    clientId: o.clientId,
    projectEndpoint: o.projectEndpoint.replace(/\/$/, ""),
    projectName,
  };
}

export async function readSeedEndpoint(): Promise<string | null> {
  try {
    const raw = await fs.readFile(getSeedPath(), "utf8");
    const parsed: unknown = parseYaml(raw);
    if (parsed && typeof parsed === "object") {
      const ep = (parsed as Record<string, unknown>).projectEndpoint;
      if (typeof ep === "string" && ep.length > 0) return ep.replace(/\/$/, "");
    }
    return null;
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code === "ENOENT") return null;
    throw e;
  }
}

async function atomicWrite(filePath: string, contents: string): Promise<void> {
  const tmp = `${filePath}.tmp`;
  await fs.writeFile(tmp, contents, "utf8");
  await fs.rename(tmp, filePath);
}

/**
 * Merge `updates` into config.yaml (preserving any existing keys), then mirror
 * the resulting projectEndpoint into foundry/seed.yaml. Atomic writes.
 */
export async function writeFoundryConfig(
  updates: Partial<Record<FoundryConfigKey, string>>,
): Promise<void> {
  await fs.mkdir(configDir(), { recursive: true });
  let existing: Record<string, unknown> = {};
  try {
    const raw = await fs.readFile(getFoundryConfigPath(), "utf8");
    const parsed: unknown = parseYaml(raw);
    if (parsed && typeof parsed === "object") existing = parsed as Record<string, unknown>;
  } catch (e) {
    if ((e as NodeJS.ErrnoException).code !== "ENOENT") throw e;
  }
  const merged = { ...existing, ...updates };
  await atomicWrite(getFoundryConfigPath(), stringifyYaml(merged, { indent: 2 }));

  const endpoint = merged.projectEndpoint;
  if (typeof endpoint === "string" && endpoint.length > 0) {
    await fs.mkdir(path.dirname(getSeedPath()), { recursive: true });
    await atomicWrite(getSeedPath(), stringifyYaml({ projectEndpoint: endpoint }, { indent: 2 }));
  }
}
